import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Bell, X, Calendar, Package, AlertTriangle, Clock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

const NOTIFICATION_COLORS = {
  high: { bg: 'bg-red-500/20', border: 'border-red-500/50', icon: 'text-red-400' },
  medium: { bg: 'bg-orange-500/20', border: 'border-orange-500/50', icon: 'text-orange-400' },
  low: { bg: 'bg-blue-500/20', border: 'border-blue-500/50', icon: 'text-blue-400' },
};

const TYPE_ICONS = {
  materiel_bestilling: Package,
  materiel_endelig: AlertTriangle,
  event: Calendar,
  custom: Clock,
};

export default function DeadlineNotifications({ eventId }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(() => {
    const saved = localStorage.getItem('dismissedDeadlines');
    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (eventId) {
      loadNotifications();
      // Tjek for nye notifikationer hvert 5. minut
      const interval = setInterval(loadNotifications, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [eventId]);

  const loadNotifications = async () => {
    if (!eventId || !token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/deadline-notifications?days_ahead=14`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data || []);
    } catch (error) {
      console.error('Fejl ved hentning af deadline-notifikationer:', error);
    }
    setLoading(false);
  };

  const dismissNotification = (id) => {
    const newDismissed = [...dismissedIds, id];
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissedDeadlines', JSON.stringify(newDismissed));
  };

  const clearDismissed = () => {
    setDismissedIds([]);
    localStorage.removeItem('dismissedDeadlines');
  };

  const activeNotifications = notifications.filter(n => !dismissedIds.includes(n.id));
  const urgentCount = activeNotifications.filter(n => n.priority === 'high').length;
  const totalCount = activeNotifications.length;

  const goToCalendar = () => {
    setShowDropdown(false);
    navigate('/kalender');
  };

  const goToMateriel = () => {
    setShowDropdown(false);
    navigate('/materiel');
  };

  const goToAddDeadline = () => {
    setShowDropdown(false);
    navigate('/kalender', { state: { openAddDeadline: true } });
  };

  if (totalCount === 0) return null;

  return (
    <div className="relative" data-testid="deadline-notifications">
      {/* Notifikations-knap */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative ${urgentCount > 0 ? 'text-red-400' : ''}`}
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {totalCount > 0 && (
          <span className={`absolute -top-1 -right-1 w-5 h-5 text-xs flex items-center justify-center rounded-full ${
            urgentCount > 0 ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'
          }`}>
            {totalCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {showDropdown && (
        <>
          {/* Overlay til at lukke dropdown */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowDropdown(false)}
          />
          
          <div 
            className="fixed left-2 right-2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden"
            data-testid="notification-dropdown"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-border bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  <span className="font-medium">Kommende deadlines</span>
                  <span className="text-xs text-muted-foreground">({totalCount})</span>
                </div>
                <div className="flex items-center gap-1">
                  {dismissedIds.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearDismissed}
                      className="h-6 text-xs"
                    >
                      Vis alle
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Notifikationer */}
            <div className="max-h-96 overflow-y-auto">
              {activeNotifications.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  Ingen aktive deadlines
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {activeNotifications.map(notification => {
                    const colors = NOTIFICATION_COLORS[notification.priority] || NOTIFICATION_COLORS.low;
                    const Icon = TYPE_ICONS[notification.type] || Clock;
                    
                    return (
                      <div 
                        key={notification.id} 
                        className={`p-3 ${colors.bg} hover:opacity-90 transition-opacity`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-1.5 rounded ${colors.border} border`}>
                            <Icon className={`h-4 w-4 ${colors.icon}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-medium truncate">{notification.title}</p>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => dismissNotification(notification.id)}
                                className="h-5 w-5 p-0 opacity-50 hover:opacity-100 shrink-0"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <span>{new Date(notification.date).toLocaleDateString('da-DK', { 
                                weekday: 'short', day: 'numeric', month: 'short' 
                              })}</span>
                              <span>•</span>
                              <span className={notification.priority === 'high' ? 'text-red-400 font-medium' : ''}>
                                {notification.days_left === 0 ? 'I dag!' : 
                                 notification.days_left === 1 ? 'I morgen' : 
                                 `${notification.days_left} dage`}
                              </span>
                            </div>
                            {notification.leverandoer && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Leverandør: {notification.leverandoer}
                              </p>
                            )}
                            {notification.description && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                {notification.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer med hurtige links */}
            <div className="px-4 py-3 border-t border-border bg-muted/30 space-y-2">
              <Button 
                variant="default" 
                size="sm" 
                onClick={goToAddDeadline}
                className="w-full"
                data-testid="notification-add-deadline-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Tilføj deadline
              </Button>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={goToCalendar}
                  className="flex-1"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Kalender
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={goToMateriel}
                  className="flex-1"
                >
                  <Package className="h-4 w-4 mr-1" />
                  Materiel
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
